import {Dialog, DialogContent, DialogTitle, Stack, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import * as React from "react";
import {useEffect} from "react";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {NotificationTrigger} from "crqlar-react-common/dist/models/sector/sector.model";
import {getSendGridSnackbarProps} from "../../../utils/reservationNotification.util";
import {LoadingButton} from "@mui/lab";
import {useMutation} from "react-query";
import CheckIcon from "@mui/icons-material/Check";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "notistack";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    reservation?: Reservation
    open: boolean
    onClose: () => void
}



const CommunicationOption = (props: {
    title: string,
    trigger: NotificationTrigger,
    reservationId: string,
    onClose: () => void
}) => {

    const {t} = useTranslation()
    const {enqueueSnackbar} = useSnackbar()
    const readOnly = useReadOnly()

    const sendMut = useMutation(
        ['resendMut', props.trigger],
        async () => {
            const response = (await ReservationService.resendNotification(props.reservationId, props.trigger)).data as {
                result: string;
                statusCode?: number | null;
            };
            console.log('response', response)
            if (response.result === 'no_template') {
                enqueueSnackbar(t('No email template configured for this event'), {variant: 'error'})
            } else if (response.result === 'error') {
                enqueueSnackbar(t('Email notification could not be sent'), {variant: 'error'})
            } else {
                const {message, variant} = getSendGridSnackbarProps(t, response.statusCode)
                enqueueSnackbar(message, {variant})
            }
        }
    )

    useEffect(() => {
        if (sendMut.isSuccess) {
            props.onClose()
        }
    }, [sendMut.isSuccess])

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
        >
            <Typography>
                {props.title}
            </Typography>
            <LoadingButton
                variant="contained"
                loading={sendMut.isLoading}
                disabled={readOnly}
                onClick={() => sendMut.mutate()}
                startIcon={sendMut.isSuccess ? <CheckIcon/> : null}
            >
                {t('Send')}
            </LoadingButton>
        </Stack>
    )
}

const EVENTS = [
    'reservationConfirmed',
    'reservationCancelled',
    'reservationUpdated',
    'externalReservationReceivedButNotConfirmed',
    'externalReservationReceivedAndConfirmed'
]

const ReservationCommunicationDialog = (props: Props) => {

    const {t} = useTranslation()
    const sector = useSector(props.reservation?.sectorId)
    const navigate = useNavigate()

    const settingsDefined = () => {
        // Commented out as the new logic has more complex calculations on API side
        // to decide if the template is defined

        // // Ensure the sector object is defined
        // if (!sector) return false
        //
        // // Ensure confirmationSettings exists within notificationSettings
        // if (!sector?.notificationSettings?.confirmationSettings) return false
        //
        // // Check if all properties in confirmationSettings are undefined
        // const allUndefined = Object.values(sector.notificationSettings.confirmationSettings).every(
        //     value => value === undefined || value === null
        // )
        //
        // if (allUndefined) return false

        // If all checks pass, settings are defined
        return true
    }

    const renderSetup = () => {
        return (
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
            >
                <Typography>
                    {t('In order to see transactional mails sent to your guests, you need to set them up first')}
                </Typography>
                <LoadingButton
                    variant="contained"
                    onClick={async () => {
                        // As we placed the transactional mail settings into separate page - change the link call
                        navigate(`/system/transactionalMailSettings/${props.reservation?.sectorId}`)
                        // window.open(`/system/sectors/${props.reservation?.sectorId}`, '_blank');
                    }}
                >
                    {t('Go to settings')}
                </LoadingButton>
            </Stack>
        )
    }

    const renderCommunicationOption = (title: string, trigger: NotificationTrigger) => {
        return (
            <CommunicationOption title={title} trigger={trigger} reservationId={props.reservation?.id!}
                                 onClose={props.onClose}/>
        )
    }

    const content = (
        <Stack
            gap={2}
        >
            {renderCommunicationOption(t('Reservation confirmed'), NotificationTrigger.CONFIRMED)}
            {renderCommunicationOption(t('Reservation cancelled'), NotificationTrigger.CANCELLED)}
            {renderCommunicationOption(t('Reservation no-show'), NotificationTrigger.NO_SHOW)}
            {renderCommunicationOption(t('Reservation completed'), NotificationTrigger.COMPLETED)}
            {renderCommunicationOption(t('Reservation updated'), NotificationTrigger.UPDATED)}
            {renderCommunicationOption(t('Widget - Received but not confirmed'), NotificationTrigger.EXTERNAL_RECEIVED_NOT_CONFIRMED)}
            {renderCommunicationOption(t('Widget - Received and confirmed automatically'), NotificationTrigger.EXTERNAL_RECEIVED_CONFIRMED)}
        </Stack>
    )

    return (
        <Dialog
            open={props.open}
            onClose={() => {
                props.onClose()
            }}
            maxWidth={false}
        >
            <DialogTitle>
                {t('Communication')}
            </DialogTitle>
            <DialogContent>
                {settingsDefined() ? content : renderSetup()}
            </DialogContent>

        </Dialog>
    )
}

export default ReservationCommunicationDialog;
